import axios from 'axios';
import { baseURL } from 'config/baseURL';
import {
  headersGET,
  headersPUT,
  headersPATCH,
  headersPOST,
  headersDELETE,
  headersFilePOST
} from 'store/utils/HTTPheaders';
import { fetchEventSource as msFetchEventSource } from '@microsoft/fetch-event-source';
import { handleUnauthorized } from 'store/utils/authFetch';
import uuidv4 from 'uuid';

// initialize global state
window.veezoo = {
  ...window.veezoo,
  // create a new session ID
  sessionId: uuidv4(),
  // the KG ID will be set by 'setGlobalKgId' as soon as / whenever the frontend sets / updates the current knowledge graph
  kgId: undefined
};

/**
 * Sets the KG ID in the global state and, if the ID changed, creates a new session ID.
 */
export const setGlobalKgId = kgId => {
  if (kgId !== window.veezoo.kgId) {
    window.veezoo.kgId = kgId;
    window.veezoo.sessionId = uuidv4();
  }
};

export const handleError = (error, dispatch) => {
  const status = error?.response?.status || null;
  if (status === 401) {
    return handleUnauthorized(dispatch);
  }
  return error;
};

const successResponse = response => ({ success: true, ...response });

/**
 * Converts the specified endpoint to a URL as follows:
 *   - prefixes the base URL to the endpoint
 *   - if the result is a root-relative URL (i.e. starting with a slash), then it removes the leading slash to make it relative
 *     (e.g. '/studio/api/users' -> 'studio/api/users')
 *   - adds the specified URL parameters to the query string
 *   - if specified, adds a 'kgId' and / or 'sessionId' URL parameter set to the respective value from 'window.veezoo'
 *
 * Using relative URLs is needed in order for the context path from the '<base>' tag to be applied for request. The
 * context path is not applied to requests with root-relative URLs.
 *
 * Note: the following two parameters, if set in the specified `params`, are treated as parameter flags and not added
 * as parameters to the URL query string:
 *   - 'addKgId': if true, a 'kgId' URL parameter is added to the query string, set to 'window.veezoo.kgId'
 *   - 'addSessionId' if true, a 'sessionId' URL parameter is added to the query string, set to 'window.veezoo.sessionId'
 *
 * @param endpoint the path of the endpoint, with a leading slash
 * @param params a JSON or 'URLSearchParams' object containing the parameters to add to the URL query string (cf. note above)
 */
const toUrl = (endpoint, params = {}) => {
  const url =
    // prefix the base URL
    (baseURL + endpoint)
      // remove leading slash (if any)
      .replace(/^\//, '');
  // split at first occurrence of '?' (cf. https://stackoverflow.com/a/4607799)
  const [urlWithoutQueryString, queryString] = url.split(/\?(.*)/s);
  // parse the query string
  const urlSearchParams = new URLSearchParams(queryString);
  // convert the params to a JSON object if they are a 'URLSearchParams' object, and extract the parameter flags
  const { addKgId, addSessionId, ...paramsToAdd } =
    params instanceof URLSearchParams ? Object.fromEntries(params) : params;
  // add the provided URL parameters
  Object.entries(paramsToAdd).forEach(([name, value]) => urlSearchParams.set(name, value));
  // set the global KG ID as the 'kgId' parameter, unless the URL already defines a 'kgId' param
  if (addKgId && !urlSearchParams.has('kgId') && window.veezoo.kgId) {
    urlSearchParams.set('kgId', window.veezoo.kgId);
  }
  // set the global session ID as the 'sessionId' parameter, unless the URL already defines a 'sessionId' param
  if (addSessionId && !urlSearchParams.has('sessionId') && window.veezoo.sessionId) {
    urlSearchParams.set('sessionId', window.veezoo.sessionId);
  }
  const newQueryString = urlSearchParams.toString();
  return urlWithoutQueryString + (newQueryString ? '?' : '') + newQueryString;
};

export const get = (endpoint, params) =>
  axios
    .get(toUrl(endpoint, params), { headers: headersGET })
    .then(successResponse)
    .catch(error => error);

// We need the HTTP request to be POST instead of GET, to send a body
export const postWithBlobResponse = (endpoint, body, params) =>
  axios
    .post(toUrl(endpoint, params), body, {
      headers: headersPOST,
      responseType: 'blob'
    })
    .then(successResponse)
    .catch(error => error);

export const put = (endpoint, body, additionalHeaders = {}, params) =>
  axios
    .put(toUrl(endpoint, params), body, { headers: { ...headersPUT, ...additionalHeaders } })
    .then(successResponse)
    .catch(error => error);

export const patch = (endpoint, body, additionalHeaders = {}, params) =>
  axios
    .patch(toUrl(endpoint, params), body, { headers: { ...headersPATCH, ...additionalHeaders } })
    .then(successResponse)
    .catch(error => error);

export const post = (endpoint, body, params) =>
  axios
    .post(toUrl(endpoint, params), body, { headers: headersPOST })
    .then(successResponse)
    .catch(error => error);

export const postFile = (endpoint, body, params) =>
  axios
    .post(toUrl(endpoint, params), body, { headers: headersFilePOST })
    .then(successResponse)
    .catch(error => error);

export const del = (endpoint, params) =>
  axios
    .delete(toUrl(endpoint, params), { headers: headersDELETE })
    .then(successResponse)
    .catch(error => error);

export const fetchGet = (endpoint, init, params) =>
  fetch(toUrl(endpoint, params), {
    method: 'GET',
    headers: headersGET,
    ...init
  });

export const fetchPost = (endpoint, body, init, params) =>
  fetch(toUrl(endpoint, params), {
    method: 'POST',
    headers: headersPOST,
    body: body && JSON.stringify(body),
    ...init
  });

export const fetchPatch = (endpoint, body, init, params) =>
  fetch(toUrl(endpoint, params), {
    method: 'PATCH',
    headers: headersPATCH,
    body: body && JSON.stringify(body),
    ...init
  });

export const fetchPut = (endpoint, body, init, params) =>
  fetch(toUrl(endpoint, params), {
    method: 'PUT',
    headers: headersPUT,
    body: body && JSON.stringify(body),
    ...init
  });

export const fetchDelete = (endpoint, init, params) =>
  fetch(toUrl(endpoint, params), {
    method: 'DELETE',
    headers: headersDELETE,
    ...init
  });

export const fetchEventSource = (endpoint, config, params) => msFetchEventSource(toUrl(endpoint, params), config);
